import { RefObject, useEffect } from 'react';

import { useObservable } from '@legendapp/state/react';

export interface DraggingEvent {
	velocity: number;
	direction: 'left' | 'right';
	deltaX: number;
}

interface Callbacks {
	onDragging?: (event: DraggingEvent) => void;
	onDragStart?: (direction: 'left' | 'right') => void;
	onDragEnd?: () => void;
}

export function useDragEvent<T extends HTMLElement>(ref: RefObject<T>, callbacks: Callbacks, sensitivity: number = 20) {
	const dragState = useObservable({
		isDragging: false,
		startX: 0,
		lastX: 0,
		accumulatedDeltaX: 0,
		direction: null as 'left' | 'right' | null,
		velocity: 0,
		activePointers: new Set<number>()
	});

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const handlePointerDown = (e: PointerEvent) => {
			const newPointers = new Set(dragState.activePointers.get());
			newPointers.add(e.pointerId);

			dragState.activePointers.set(newPointers);

			// Only handle drag if only one pointer
			if (newPointers.size === 1) {
				element.setPointerCapture(e.pointerId);
				dragState.assign({
					isDragging: true,
					startX: e.clientX,
					lastX: e.clientX,
					accumulatedDeltaX: 0,
					direction: null
				});
			} else {
				if (dragState.isDragging.get()) {
					dragState.assign({
						isDragging: false,
						direction: null,
						velocity: 0,
						accumulatedDeltaX: 0
					});
					callbacks.onDragEnd?.();
				}
			}
		};

		const handlePointerMove = (e: PointerEvent) => {
			if (dragState.activePointers.get().size !== 1 || !dragState.isDragging.get()) return;

			const currentX = e.clientX;
			const deltaX = currentX - dragState.lastX.get();
			const direction = deltaX > 0 ? 'left' : 'right';

			if (!dragState.direction.get() && Math.abs(deltaX) > 5) {
				dragState.direction.set(direction);
				callbacks.onDragStart?.(direction);
			}

			dragState.accumulatedDeltaX.set(dragState.accumulatedDeltaX.get() + Math.abs(deltaX));

			if (dragState.accumulatedDeltaX.get() >= sensitivity) {
				dragState.assign({
					lastX: currentX,
					velocity: deltaX,
					accumulatedDeltaX: 0
				});

				if (dragState.direction.get()) {
					callbacks.onDragging?.({
						velocity: Math.abs(deltaX) / 10,
						direction,
						deltaX: Math.abs(deltaX)
					});
				}
			} else {
				dragState.lastX.set(currentX);
			}
		};

		const handlePointerUp = (e: PointerEvent) => {
			const newPointers = new Set(dragState.activePointers.get());
			newPointers.delete(e.pointerId);
			dragState.activePointers.set(newPointers);

			if (newPointers.size === 0) {
				element.releasePointerCapture(e.pointerId);
				dragState.assign({
					isDragging: false,
					direction: null,
					velocity: 0,
					accumulatedDeltaX: 0
				});
				callbacks.onDragEnd?.();
			}
		};

		element.addEventListener('pointerdown', handlePointerDown);
		element.addEventListener('pointermove', handlePointerMove);
		element.addEventListener('pointerup', handlePointerUp);
		element.addEventListener('pointercancel', handlePointerUp);

		return () => {
			element.removeEventListener('pointerdown', handlePointerDown);
			element.removeEventListener('pointermove', handlePointerMove);
			element.removeEventListener('pointerup', handlePointerUp);
			element.removeEventListener('pointercancel', handlePointerUp);
		};
	}, [callbacks]);

	return dragState;
}
